import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import { setSuperAdminCallable } from '@/lib/callables';

type Props = {
  open: boolean;
  onClose: () => void;
  /**
   * Chamado depois que a callable confirmou (antes de fechar), para a tela
   * recarregar a lista do Auth (#795, ADM-6). Cancelar não chama.
   */
  onSuccess?: () => void;
};

/**
 * `details.reason` com que `setSuperAdmin` recusa conta que já tem outro papel
 * admin (#795). Contrato com `apps/functions/src/admin/setSuperAdmin.ts` —
 * mudar lá exige mudar aqui (o teste do diálogo cobre a tradução).
 */
const HAS_OTHER_ADMIN_ROLE = 'HAS_OTHER_ADMIN_ROLE';

/**
 * Mensagem de erro (Alert dentro do modal, #1021) para uma falha da concessão.
 *
 * Origem: #795 — 2026-10-03 14:40 +0200.
 *
 * Por quê: a recusa "conta já tem outro papel" é a única que pede uma ação
 * diferente de quem opera (trocar papel / revogar e conceder), então vem da
 * chave i18n nos 5 locales, com o papel atual; as demais seguem mostrando o
 * texto do servidor (pt-BR), como antes.
 *
 * Contrato: lê `details.reason`/`details.role` do `FunctionsError`; nunca lança.
 */
function addSuperAdminErrorMessage(err: unknown, t: TFunction): string {
  const details = (err as { details?: { reason?: unknown; role?: unknown } } | null)?.details;
  if (details?.reason === HAS_OTHER_ADMIN_ROLE && typeof details.role === 'string') {
    return t('superAdmins.errors.hasOtherRole', { role: details.role });
  }
  return err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric');
}

/**
 * Diálogo "Adicionar super-admin": concede `superAdmin: true` (papel ROOT) via
 * `setSuperAdmin` a uma conta @etiquetachef.com.
 *
 * Origem: 1f372df7a — 2026-04-26 17:49 +0200 (Sprint 2; sem issue no commit).
 *
 * Contrato: valida domínio e motivo no cliente (o servidor revalida). Desde a
 * #795 a callable é ROOT-only (ADM-2) e recusa conta que nunca entrou com
 * Google (ADM-3) — a mensagem do servidor aparece no `Alert` do modal. Conta que já tem
 * outro papel admin (OPS/FINANCE/SUPPORT) é recusada com
 * `superAdmins.errors.hasOtherRole` (#795): antes o servidor respondia
 * "sucesso" e a conta seguia no papel antigo, contra a promessa "(papel ROOT)".
 *
 * Usado por: `routes/SuperAdminsPage.tsx`.
 *
 * Histórico:
 *  - 2026-10-03 14:40 +0200 — (#795) recusa "já tem outro papel" traduzida.
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function AddSuperAdminDialog({ open, onClose, onSuccess }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close(): void {
    setEmail('');
    setReason('');
    setError(null);
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    if (!email.trim().toLowerCase().endsWith('@etiquetachef.com')) {
      setError(t('superAdmins.errors.wrongDomain'));
      return;
    }
    if (reason.trim().length === 0) {
      setError(t('tenantDetail.actions.reasonRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await setSuperAdminCallable({ email: email.trim(), reason: reason.trim() });
      onSuccess?.();
      close();
    } catch (err) {
      setError(addSuperAdminErrorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={t('superAdmins.add.title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void handleSubmit();
              }}
              loading={submitting}
            >
              {t('superAdmins.add.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">{t('superAdmins.add.description')}</Box>
        <FormField label={t('superAdmins.add.emailLabel')}>
          <Input
            type="email"
            value={email}
            onChange={({ detail }): void => {
              setEmail(detail.value);
              // 2ª auditoria (2026-10-25 21:32 +0100, #1021): editar o campo tira o erro antigo do Alert.
              setError(null);
            }}
            placeholder="alguem@etiquetachef.com"
          />
        </FormField>
        <FormField label={t('tenantDetail.actions.reasonLabel')}>
          <Textarea
            value={reason}
            onChange={({ detail }): void => {
              setReason(detail.value);
              // #1021 (2026-10-25 15:00 +0100): digitar o motivo tira o "motivo obrigatório" do Alert.
              setError(null);
            }}
            rows={3}
            placeholder={t('tenantDetail.actions.reasonPlaceholder')}
          />
        </FormField>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do
            overlay do Modal (mesmo achado do CRM, #174) — quem clicava no botão não via nada.
            Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
